import React from 'react';
import { Button, Input } from 'semantic-ui-react';
import { formatUrl } from '@plone/volto/helpers/Api/Api';
import {
  resolveSocialNetwork,
  UNKNOWN_NETWORK,
} from 'volto-ministerio-publico-federal/helpers/Social/Social';
import {
  isSocialGlyphToken,
  SocialIcon,
  SOCIAL_LABELS,
} from 'volto-ministerio-publico-federal/components/Icons/social';

/** O que o serviço `@social-link-preview` devolve. */
export interface SocialPreview {
  network: string;
  status:
    | 'ok'
    | 'unsupported'
    | 'requires_token'
    | 'manual_only'
    | 'timeout'
    | 'error';
  title: string | null;
  image: string | null;
  is_video: boolean;
  video_id: string | null;
  cached: boolean;
  message?: string;
}

export interface SocialUrlWidgetProps {
  id: string;
  title?: string;
  description?: string;
  value?: string;
  onChange: (id: string, value: string) => void;
  /** Chamado com o resultado da busca. Quem decide o que gravar é o schema. */
  onFetch?: (preview: SocialPreview) => void;
  [key: string]: unknown;
}

type FetchState = 'idle' | 'loading' | 'done';

/**
 * Campo de URL do post, com detecção de rede e busca de metadados.
 *
 * Três coisas acontecem aqui, e só uma depende do servidor:
 *
 * 1. **Detecção da rede** — regex sobre o domínio, síncrona. O ícone aparece
 *    no instante em que a URL é colada, sempre, mesmo com o backend fora do ar.
 * 2. **Busca de título e capa** — só sob clique. Nunca ao digitar (dispararia
 *    uma chamada por tecla) nem ao salvar (amarraria o salvamento da capa
 *    inteira à latência de um terceiro).
 * 3. **Aviso de rede sem suporte** — Instagram, Facebook e LinkedIn não
 *    publicam esses dados sem app registrado ou login. Aqui isso vira uma
 *    mensagem explicando o que fazer, não um erro genérico.
 *
 * Título e capa continuam editáveis à mão em qualquer cenário: este campo é
 * conveniência, não caminho obrigatório.
 */
const SocialUrlWidget: React.FC<SocialUrlWidgetProps> = ({
  id,
  title,
  description,
  value,
  onChange,
  onFetch,
}) => {
  const [state, setState] = React.useState<FetchState>('idle');
  const [feedback, setFeedback] = React.useState<{
    tone: 'ok' | 'warn' | 'error';
    text: string;
  } | null>(null);

  const network = resolveSocialNetwork(value);
  const known = isSocialGlyphToken(network) ? network : undefined;

  // Uma busca em voo continua válida enquanto o campo não mudar. Se o editor
  // trocar a URL no meio, o resultado que chegar é de outra URL e tem de ser
  // descartado.
  const inFlightFor = React.useRef<string | null>(null);

  React.useEffect(() => {
    setFeedback(null);
    setState('idle');
  }, [value]);

  const handleFetch = React.useCallback(async () => {
    const target = (value ?? '').trim();
    if (!target) return;

    inFlightFor.current = target;
    setState('loading');
    setFeedback(null);

    try {
      const endpoint = formatUrl(
        `/@social-link-preview?url=${encodeURIComponent(target)}`,
      );
      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (inFlightFor.current !== target) return;

      if (!response.ok) {
        setFeedback({
          tone: 'error',
          text: 'Não foi possível buscar os dados agora. Preencha manualmente.',
        });
        return;
      }

      const preview = (await response.json()) as SocialPreview;
      if (inFlightFor.current !== target) return;

      if (preview.status === 'ok') {
        onFetch?.(preview);
        setFeedback({
          tone: 'ok',
          text: preview.title
            ? 'Dados preenchidos. Ajuste o que precisar.'
            : 'Capa preenchida. O título desta rede precisa ser escrito à mão.',
        });
      } else {
        // A mensagem vem do servidor, que é quem sabe por que não deu — rede
        // sem suporte, timeout ou falha do provedor pedem orientações
        // diferentes.
        setFeedback({
          tone:
            preview.status === 'error' || preview.status === 'timeout'
              ? 'error'
              : 'warn',
          text: preview.message ?? 'Preencha título e capa manualmente.',
        });
        // Mesmo sem título, o YouTube devolve a capa derivada do id do vídeo.
        if (preview.image) onFetch?.(preview);
      }
    } catch {
      if (inFlightFor.current !== target) return;
      setFeedback({
        tone: 'error',
        text: 'Não foi possível buscar os dados agora. Preencha manualmente.',
      });
    } finally {
      if (inFlightFor.current === target) setState('done');
    }
  }, [value, onFetch]);

  const feedbackColor =
    feedback?.tone === 'ok'
      ? '#046C4E'
      : feedback?.tone === 'warn'
        ? '#8A2C0D'
        : '#9B1C1C';

  return (
    <div className="field mpf-social-url-widget">
      {title ? (
        <label htmlFor={`field-${id}`} style={{ fontWeight: 600 }}>
          {title}
        </label>
      ) : null}

      <Input
        id={`field-${id}`}
        type="url"
        inputMode="url"
        value={value ?? ''}
        placeholder="https://…"
        onChange={(_event, data) => onChange(id, data.value)}
        fluid
      />

      {description ? (
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.85em', opacity: 0.8 }}>
          {description}
        </p>
      ) : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginTop: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        {known ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
            }}
          >
            <SocialIcon network={known} variant="brand" size={20} />
            <span style={{ fontSize: '0.85em' }}>{SOCIAL_LABELS[known]}</span>
          </span>
        ) : value ? (
          <span style={{ fontSize: '0.85em', opacity: 0.75 }}>
            Rede não reconhecida
          </span>
        ) : null}

        <Button
          type="button"
          size="tiny"
          basic
          disabled={
            !value || state === 'loading' || network === UNKNOWN_NETWORK
          }
          loading={state === 'loading'}
          onClick={handleFetch}
        >
          Buscar dados
        </Button>
      </div>

      {feedback ? (
        <p
          role="status"
          style={{
            margin: '0.4rem 0 0',
            fontSize: '0.85em',
            color: feedbackColor,
          }}
        >
          {feedback.text}
        </p>
      ) : null}
    </div>
  );
};

export default SocialUrlWidget;
